import { api } from '../../../shared/lib/axios';
import type { Bond, BondStatus } from './index';

/** Lotação, movimentações e situação funcional (change `pessoas-vida-funcional`). */

export interface Ref { id: string; code?: string; name: string }

export interface Assignment {
  id: string;
  validFrom: string;
  validTo: string | null;
  workMode: 'presencial' | 'hibrido' | 'remoto';
  movementId: string | null;
  orgUnit: Ref;
  jobPosition: Ref;
  costCenter: Ref | null;
  location: Ref | null;
  manager: Ref | null;
}

export interface Movement {
  id: string;
  bondId: string;
  type: string;
  typeLabel: string;
  effectiveDate: string;
  changes: Record<string, unknown>;
  previous: Record<string, unknown> | null;
  justification: string | null;
  status: 'agendada' | 'aguardando_aprovacao' | 'efetivada' | 'cancelada';
  requestedBy: string | null;
  approvedBy: string | null;
  cancelledReason: string | null;
  lastError: string | null;
  createdAt: string;
}

export interface PendingMovement {
  id: string;
  bondId: string;
  type: string;
  typeLabel: string;
  effectiveDate: string;
  changes: Record<string, unknown>;
  justification: string | null;
  requestedBy: string | null;
  requestedByName: string | null;
  userId: string;
  personName: string;
  internalRegistration: string;
  createdAt: string;
}

export interface MovementPayload {
  type: string;
  effectiveDate: string;
  justification?: string;
  orgUnitId?: string;
  jobPositionId?: string;
  costCenterId?: string;
  locationId?: string;
  managerUserId?: string;
  workMode?: string;
  salaryAmount?: number;
  contractKind?: string;
  contractEndDate?: string;
  probationEndDate?: string;
  probationExtensionEndDate?: string;
}

export interface StatusReason { id: string; code: string; name: string; status: string; active: boolean }

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

export async function lotacoesDoVinculo(bondId: string): Promise<{ current: Assignment | null; history: Assignment[] }> {
  const { data } = await api.get(`/employment-bonds/${bondId}/assignments`);
  const b = object<{ current?: Assignment | null; history?: Assignment[] }>(data);
  return { current: b.current ?? null, history: b.history ?? [] };
}

export async function movimentacoesDoVinculo(bondId: string): Promise<Movement[]> {
  const { data } = await api.get(`/employment-bonds/${bondId}/movements`);
  return list<Movement>(data);
}

export async function registrarMovimentacao(bondId: string, payload: MovementPayload): Promise<Movement> {
  const { data } = await api.post(`/employment-bonds/${bondId}/movements`, payload);
  return object<Movement>(data);
}

export async function aprovarMovimentacao(id: string): Promise<Movement> {
  const { data } = await api.post(`/employment-movements/${id}/approve`);
  return object<Movement>(data);
}

export async function cancelarMovimentacao(id: string, justification: string): Promise<Movement> {
  const { data } = await api.post(`/employment-movements/${id}/cancel`, { justification });
  return object<Movement>(data);
}

export async function movimentacoesPendentes(): Promise<PendingMovement[]> {
  const { data } = await api.get('/employment-movements/pending-approval');
  return list<PendingMovement>(data);
}

export async function alterarSituacao(
  bondId: string, payload: { status: BondStatus; justification: string; reasonId?: string; date?: string },
): Promise<Bond> {
  const { data } = await api.post(`/employment-bonds/${bondId}/status`, payload);
  return object<Bond>(data);
}

export async function listarMotivos(status?: string, all = false): Promise<StatusReason[]> {
  const { data } = await api.get('/bond-status-reasons', { params: { status, all: String(all) } });
  return list<StatusReason>(data);
}

export async function salvarMotivo(id: string | null, payload: Partial<StatusReason>): Promise<StatusReason> {
  const { data } = id ? await api.patch(`/bond-status-reasons/${id}`, payload) : await api.post('/bond-status-reasons', payload);
  return object<StatusReason>(data);
}

// --- Opções de estrutura (selects do formulário) -------------------

export interface StructureOption { id: string; code: string; name: string }

async function options(path: string): Promise<StructureOption[]> {
  const { data } = await api.get(path, { params: { limit: 500 } });
  return list<StructureOption>(data);
}

export const listarAreasAtivas = () => options('/org-units');
export const listarCargosAtivos = () => options('/job-positions');
export const listarCentrosDeCustoAtivos = () => options('/cost-centers');
export const listarLocalidadesAtivas = () => options('/locations');
